import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ProductAlias, ProductAliasType } from '../models/product-alias';
import { Product } from '../models/product';
import { ProductPricing } from '../models/product-pricing';
import { ProductAvailability } from '../models/product-availability';
import { environment } from '../../environments/environment';
import { ProductSetupRequest } from '../models/product-setup-request';
import { PurchasePlan } from '../models/purchase-plan';

@Injectable()
export class ProductAliasService {

  baseUrl = environment.productAliasServiceBaseUrl;

  constructor(private http: HttpClient) {
  }

  search(term: string, marketId: string): Observable<ProductAlias[]> {
    let encodedTerm = encodeURIComponent(term);
    const url = encodeURI(`${this.baseUrl}search/searchTerm/${encodedTerm}?marketId=${marketId}`);
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<ProductAlias[]>(url, { headers });
  }

  searchV2(term: string, accountNumber: string, marketType?: string): Observable<ProductAlias[]> {
    let encodedTerm = encodeURIComponent(term);
    const url = encodeURI(`${this.baseUrl}v2/products/search/searchTerm/${encodedTerm}?accountNumber=${accountNumber}&marketType=${marketType}`);
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<ProductAlias[]>(url, { headers });
  }

  searchProductUsingOrgId(term: string, orgId: string): Observable<ProductAlias[]> {
    let encodedTerm = encodeURIComponent(term);
    const url = encodeURI(`${this.baseUrl}v2/products/search/searchTerm/${encodedTerm}?orgId=${orgId}`);
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<ProductAlias[]>(url, { headers });
  }

  searchProductForMasterBom(term: string, orgId: string, accountNumbers: string [], marketType?: string): Observable<ProductAlias[]> {
    const url = encodeURI(`${this.baseUrl}v2/products/search/searchTermForBom`);
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<ProductAlias[]>(url, { searchTerm: term, orgId: orgId, accountNumbers: accountNumbers , marketType: marketType}, { headers });
  }

  create(product: ProductAlias): Observable<ProductAlias> {
    const urlMapper: { [id in ProductAliasType]?: string } = {
      Manufacture: `${this.baseUrl}manufacturerAlias`,
      Consignment: `${this.baseUrl}consignmentAlias`,
      Customer: `${this.baseUrl}customerAlias`,
      Placeholder: `${this.baseUrl}placeholderAlias`
    };

    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<ProductAlias>(urlMapper[product.type], product, { headers });
  }

  productDetails(partNumbers: string[], marketId?: string): Observable<Product[]> {
    const url = `${this.baseUrl}v2/products/details`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<any>(url, { skuList: partNumbers, marketId: marketId }, { headers })
      .map(res => res.products || []);
  }

  productDetailsV2(partNumbers: string[], accountNumber: string, orgId?: string, isPricingRequried?: boolean, marketType?: string): Observable<Product[]> {
    const url = `${this.baseUrl}v2/products`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<any>(url, { Skus: partNumbers, AccountNumber: accountNumber, OrgId: orgId, 
                                      isPricingRequried: isPricingRequried , marketType: marketType }, { headers })
      .map(res => res.products || []);
  }

  priceLookup(worksheetId: string, products: { sku: string, quantity: number }[], accountNumber?: string): Observable<ProductPricing[]> {
    const url = `${this.baseUrl}product/pricing`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<any>(url, { worksheetId: worksheetId, skuDetails: products, accountNumber: accountNumber }, { headers })
      .map(res => res.pricingInformation || []);
  }

  productAvailability(skus: string[]): Observable<ProductAvailability[]> {
    const url = `${this.baseUrl}product/availability`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<any>(url, { skus: skus }, { headers })
      .map(res => res.productAvailability || []);
  }

  createProductRequest(request: ProductSetupRequest) {
    const url = `${this.baseUrl}v2/products/productSetupRequest`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<any>(url, request, { headers });
  }

  findIncompatibleProducts(skus: string[], purchasePlanCode: string) {
    const url = `${this.baseUrl}product/getIncompatibleProducts`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.post<string[]>(url, { skuList: skus.join(','), purchasePlanCode: purchasePlanCode }, { headers });
  }

  getProductMinInfo(skus: string[], accountNumber: string) {
    const url = `${this.baseUrl}v2/products/mininfo`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Content-Type', 'application/json');
    return this.http.post<Product[]>(url, { Skus: skus, AccountNumber: accountNumber, }, { headers });
  }

  getPurchasePlans(accountNumber: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/purchaseplan/list`;
    const params = new HttpParams()
      .set('accountNumber', accountNumber);
    return this.http.get<PurchasePlan[]>(url, { params, headers });
  }

  getWorksheetPurchasePlan(accountNumber: string, purchasePlanCode: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}v2/purchaseplan`;
    const params = new HttpParams()
      .set('accountNumber', accountNumber)
      .set('purchasePlanCode', purchasePlanCode);
    return this.http.get<PurchasePlan>(url, { params, headers });
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3408204168/src/bitbucket.org/tesscotds/webclient/src/api/services/product-alias.service.ts